import { createFileRoute } from "@tanstack/react-router";
import ArticlePoolCopingMaterials from "@/pages/ArticlePoolCopingMaterials";
import { buildHead } from "@/lib/seo";
import { FEATURED_IMAGE_POOL_COPING_MATERIALS } from "@/data/poolCopingMaterialsData";
import { FAQS_POOL_COPING_MATERIALS } from "@/data/poolCopingMaterialsData";

const PATH = "/blog/best-pool-coping-materials-south-florida";
const CANONICAL = `https://precisionpaverspools.com${PATH}`;

// BlogPosting schema. The `image` field is included only once a verified
// featured-image URL has been supplied, so the structured data never
// references an empty value.
const blogPosting: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline:
    "Best Pool Coping Materials for South Florida: How to Choose the Right Pool Edge",
  description:
    "Compare pool coping materials for South Florida, including travertine, marble, concrete, paver, porcelain, and other options by heat, traction, maintenance, appearance, and project fit.",
  datePublished: "2026-10-20",
  dateModified: "2026-10-20",
  author: {
    "@type": "Organization",
    name: "Precision Pavers & Pool Design Team",
  },
  publisher: {
    "@type": "Organization",
    name: "Precision Pavers & Pool Design",
    url: "https://precisionpaverspools.com/",
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": CANONICAL,
  },
};

if (FEATURED_IMAGE_POOL_COPING_MATERIALS) {
  blogPosting.image = FEATURED_IMAGE_POOL_COPING_MATERIALS;
}

const jsonLd: Record<string, unknown>[] = [
  blogPosting,
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://precisionpaverspools.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://precisionpaverspools.com/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Best Pool Coping Materials for South Florida",
        item: CANONICAL,
      },
    ],
  },
];

// FAQPage schema is emitted only when visible FAQs exist, so the schema
// always exactly matches the on-page FAQ content. During the preparation
// phase the shared FAQ array is empty, so no FAQPage is emitted yet.
if (FAQS_POOL_COPING_MATERIALS.length > 0) {
  jsonLd.push({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS_POOL_COPING_MATERIALS.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  });
}

export const Route = createFileRoute(
  "/blog/best-pool-coping-materials-south-florida",
)({
  head: () =>
    buildHead({
      title: "Best Pool Coping Materials for South Florida",
      description:
        "Compare pool coping materials for South Florida, including travertine, marble, concrete, paver, porcelain, and other options by heat, traction, maintenance, appearance, and project fit.",
      path: PATH,
      ogType: "article",
      ogImage: FEATURED_IMAGE_POOL_COPING_MATERIALS || undefined,
      jsonLd,
    }),
  component: ArticlePoolCopingMaterials,
});
